import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  companyInformation,
  LINK_LANDING_CONTACT,
  LINK_BOOK_DEMO_VIDEO_EN,
  LINK_BOOK_DEMO_VIDEO_ES,
  PLAN_ENTERPRISE,
  RECURRENT_MULTIPLIER,
  LEGACY_CURRENT_PLANS,
  ALERT_ICON_TYPE_SUCCESS,
  AGENCIES_WITH_HIDDEN_PREVIOUS_PLANS,
} from "../../utils/constants";
import Typography from "@mui/material/Typography";
import { useSelectorApp, useDispatchApp } from "../../lib/redux";
import {
  startChangeSubscriptionPlanStripe,
  startCreateUpdatePaymentMethodLinkAds,
  startGetPreviewProrationAdsSubscriptionSimple,
  clearProrationPrices,
} from "../../actions/payments";
import {
  BUTTON_YOU_HAVE_DOUBTS,
  BUTTON_CHANGE_PAYMENT_METHODS,
  LOADING_PLAN_INFORMATION,
  IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE,
  UPGRADE_SUBSCRIPTION_SUCCESSFUL,
} from "../../i18n/keysTranslations";
import { capitalizeFirstLetter, openLink } from "../../utils/string";
import PricingHero from "./PricingHero";
import PricingCardsContainer from "./PricingCardsContainer";
import Loader from "../Loaders/Loader";
import { buildBenefitsFromConfig, usePlanType } from "./plansConfiguration";
import { getPlanKeysForVersion } from "../../utils/creditActionCosts";

import { useTranslationApp } from "../../lib/i18next";
import { SimpleAlert } from "../Alerts/Alerts";
import { onInit3DSecureAuth } from "../../services/stripe";
import { useStripe } from "@stripe/react-stripe-js";
import { getPathDatabaseByRole } from "../../actions/auth";
import { fetchAgencyStripeCheckoutPromo } from "../../actions/promotionalCodes";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";

const isAppSumoLifetimeRecurrent = (recurrent) =>
  recurrent === "yearly" || recurrent === "year";

const buildOrderPlansForCatalog = (creditPricingVersion, allPlans) => {
  const versionOrder = [...getPlanKeysForVersion(creditPricingVersion)];
  const legacyKeysOnAccount = LEGACY_CURRENT_PLANS.filter(
    (key) => allPlans?.[key] && !versionOrder.includes(key)
  );
  return [...versionOrder, ...legacyKeysOnAccount, PLAN_ENTERPRISE];
};

const getSubscriptionTierRelation = (subscriptionPlan, planKey, orderPlans) => {
  if (!subscriptionPlan) return null;
  const subIdx = orderPlans.indexOf(subscriptionPlan);
  const planIdx = orderPlans.indexOf(planKey);
  if (subIdx === -1 || planIdx === -1) return null;
  if (planIdx < subIdx) return "below";
  if (planIdx === subIdx) return "current";
  return "above";
};

/**
 * @param {object} props
 * @param {boolean} [props.showAllSubscriptionTiers=true] If true, all tiers are listed and
 *   subscriptionTierRelation drives CTAs (none below, disabled current, CTA above). If false,
 *   only tiers above the active plan are shown (legacy).
 */
const Pricing = ({
  sx = {},
  onCallbackFinish = () => {},
  onCloseModal = () => {},
}) => {
  const [recurrent, setRecurrent] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [checkoutPromo, setCheckoutPromo] = useState(null);
  const [isCheckoutPromoLoading, setIsCheckoutPromoLoading] = useState(false);
  const { plans: allPlans, creditPricingVersion } = useSelectorApp(
    (state) => state.payments
  );
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const stripe = useStripe();

  const { agencyID } = dispatch(getPathDatabaseByRole());
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();

  const showAllSubscriptionTiers =
    !AGENCIES_WITH_HIDDEN_PREVIOUS_PLANS.includes(agencyID);

  const { typeOfPlans, currentTypeOfPlan, isTypeOfPlansLoaded } = usePlanType();

  const stripeCustomerID = useSelectorApp(
    (state) => state.payments.stripe.stripeCustomerID
  );
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const { recurrent: subscriptionPlanRecurrent } = subscriptionPlanInfo;

  // AppSumo lifetime (yearly): convert to any normal monthly Stripe plan — no tier
  // progression, no proration; checkout as a new monthly subscription.
  const isAppSumoLifetimeConversion =
    isAppSumoResolved &&
    isAppSumo &&
    Boolean(subscriptionPlan) &&
    isAppSumoLifetimeRecurrent(subscriptionPlanRecurrent);

  const multiplier = RECURRENT_MULTIPLIER[recurrent];
  const isUpgrade = Boolean(subscriptionPlan) && !isAppSumoLifetimeConversion;

  const loadCheckoutPromo = useCallback(async () => {
    if (!isAppSumoResolved) return;
    if (isUpgrade) {
      setCheckoutPromo(null);
      return;
    }
    setIsCheckoutPromoLoading(true);
    const promo = await dispatch(fetchAgencyStripeCheckoutPromo());
    setCheckoutPromo(promo?.ok ? (promo.data ?? null) : null);
    setIsCheckoutPromoLoading(false);
  }, [dispatch, isUpgrade, isAppSumoResolved]);

  useEffect(() => {
    loadCheckoutPromo();
  }, [loadCheckoutPromo]);

  const onGetProrationPrices = () => {
    if (!isAppSumoResolved) return;

    if (isAppSumoLifetimeConversion) {
      setRecurrent("monthly");
      dispatch(clearProrationPrices());
      return;
    }

    if (subscriptionPlan) {
      setRecurrent(subscriptionPlanRecurrent);
    }

    const shouldFetchProration = isUpgrade;

    shouldFetchProration
      ? dispatch(startGetPreviewProrationAdsSubscriptionSimple())
      : dispatch(clearProrationPrices());
  };

  useEffect(() => {
    if (!isAppSumoResolved || !isTypeOfPlansLoaded) {
      setIsLoading(true);
      return;
    }

    const plansFormatted = [];
    const ORDER_PLANS = buildOrderPlansForCatalog(
      creditPricingVersion,
      allPlans
    );

    const allPlansFormatted = {
      ...allPlans,
      [PLAN_ENTERPRISE]: {
        benefits: {},
      },
    };

    Object.keys(allPlansFormatted).forEach((key) => {
      if (
        !isAppSumoLifetimeConversion &&
        !showAllSubscriptionTiers &&
        subscriptionPlan &&
        ORDER_PLANS.indexOf(key) <= ORDER_PLANS.indexOf(subscriptionPlan)
      ) {
        return;
      }

      if (!ORDER_PLANS.includes(key)) return;

      const plan = allPlansFormatted[key];
      const benefits = plan.benefits;

      const benefitsFormatted = buildBenefitsFromConfig(
        currentTypeOfPlan,
        key,
        benefits,
        multiplier,
        creditPricingVersion
      );

      const objectPlan = {
        ...plan,
        id: key,
        name: capitalizeFirstLetter(key),
        benefits: benefitsFormatted,
        credits: benefits.credits,
        coins: benefits.coins,
        subscriptionTierRelation:
          !isAppSumoLifetimeConversion && showAllSubscriptionTiers
            ? getSubscriptionTierRelation(subscriptionPlan, key, ORDER_PLANS)
            : null,
      };

      plansFormatted.push(objectPlan);
    });

    plansFormatted.sort((a, b) => {
      return ORDER_PLANS.indexOf(a.id) - ORDER_PLANS.indexOf(b.id);
    });

    setPlans(plansFormatted);
    setIsLoading(false);
    // eslint-disable-next-line
  }, [
    allPlans,
    subscriptionPlan,
    recurrent,
    typeOfPlans,
    isUpgrade,
    currentTypeOfPlan,
    showAllSubscriptionTiers,
    creditPricingVersion,
    isAppSumoResolved,
    isAppSumoLifetimeConversion,
  ]);

  useEffect(() => {
    onGetProrationPrices();
    // eslint-disable-next-line
  }, [
    subscriptionPlan,
    recurrent,
    isUpgrade,
    currentTypeOfPlan,
    isAppSumoResolved,
    isAppSumoLifetimeConversion,
  ]);
  useEffect(() => {
    return () => {
      dispatch(clearProrationPrices());
    };
    // eslint-disable-next-line
  }, []);

  const onUpgradeSubscription = async ({ planID, paymentIntent }) => {
    let response = await dispatch(
      startChangeSubscriptionPlanStripe({
        planID,
        paymentIntent,
      })
    );

    if (response.ok) {
      onCallbackFinish();

      SimpleAlert({
        title: t(UPGRADE_SUBSCRIPTION_SUCCESSFUL),
        text: t(IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return {
        ok: true,
      };
    }
    if (!response.ok && response.status === "requiresAction") {
      const responseVerification = await onInit3DSecureAuth({
        clientSecret: response.clientSecret,
        stripe,
        onCallbackFailed: onCloseModal,
      });
      if (responseVerification.ok) {
        const paymentIntent = responseVerification.paymentIntent;
        await onUpgradeSubscription({ planID, paymentIntent });
        return { ok: true };
      }
    }
    return { ok: false };
  };

  const onManagePaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    const response = await dispatch(startCreateUpdatePaymentMethodLinkAds({}));
    if (response.ok && response.data?.url) {
      window.location.href = response.data.url;
    }
    setIsLoadingPaymentMethods(false);
  };

  const onOpenBookDemo = () => {
    const isSpanish = i18n.language?.startsWith("es");
    openLink(isSpanish ? LINK_BOOK_DEMO_VIDEO_ES : LINK_BOOK_DEMO_VIDEO_EN);
  };

  return (
    <Box sx={sx}>
      <PricingHero />
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 370,
          }}
        >
          <Loader
            size={40}
            hasMessage={true}
            message={t(LOADING_PLAN_INFORMATION)}
          />
        </Box>
      ) : (
        <PricingCardsContainer
          isUpgrade={isUpgrade}
          onUpgradeSubscription={onUpgradeSubscription}
          recurrent={recurrent}
          onChangeRecurrent={setRecurrent}
          plans={plans}
          typeOfPlan={currentTypeOfPlan}
          onOpenBookDemo={onOpenBookDemo}
          checkoutPromo={checkoutPromo}
          isCheckoutPromoLoading={isCheckoutPromoLoading}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: 2,
          mx: 1,
          gap: 1,
        }}
      >
        <Typography
          variant="body1"
          align="center"
          onClick={() => openLink(LINK_LANDING_CONTACT)}
          sx={{
            textDecoration: "underline",
            fontSize: "1.1rem",
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
            },
          }}
        >
          {t(BUTTON_YOU_HAVE_DOUBTS)}
        </Typography>
        {stripeCustomerID && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {isLoadingPaymentMethods && (
              <Loader size={18} sx={{ mt: 0 }} hasMessage={false} />
            )}
            <Typography
              variant="body1"
              align="center"
              onClick={onManagePaymentMethods}
              sx={{
                textDecoration: "underline",
                fontSize: "1.1rem",
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "underline",
                },
              }}
            >
              {t(BUTTON_CHANGE_PAYMENT_METHODS)}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" align="center">
            {`${companyInformation.name} - A Delaware LLC`}
          </Typography>
          <Typography variant="body2" align="center">
            {companyInformation.address}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Pricing;
